// Ocean crossings (GDD §4, ROADMAP D31): strategic chords the World War network always carries across the open ocean.
// The network's spanning tree joins the Americas to the rest of the world by its single shortest link (Fortaleza–Praia),
// so without these the expansion pull measured a war between North America and Europe or Asia through Brazil and West
// Africa. Each leg is a World network candidate (WORLD_NETWORK_CANDIDATES, the spherical Delaunay pairs), so the network
// stays planar. The strategic network is only the expansion pull's optimistic distance (PathService.biasGraph): nobody
// travels these legs. Land units cross water only aboard transports (docs/TRANSPORT.md); ships sail the sea lanes.

import { ContentValidationError } from './errors'
import { ALL_CITIES, COUNTRIES, WORLD_NETWORK_CANDIDATES, type City, type CountryCode } from './geo'

export interface CityRef {
  readonly country: string
  readonly city: string
}

export interface OceanCrossing {
  readonly name: string
  /** Two or more stops; each consecutive pair is one sea edge of the network. */
  readonly stops: readonly CityRef[]
}

export const OCEAN_CROSSINGS: readonly OceanCrossing[] = [
  {
    name: 'North Atlantic crossing',
    stops: [
      { country: 'USA', city: 'Boston' },
      { country: 'PRT', city: 'Ponta Delgada' },
      { country: 'PRT', city: 'Lisbon' },
    ],
  },
  {
    name: 'Arctic crossing',
    stops: [
      { country: 'CAN', city: 'Québec' },
      { country: 'ISL', city: 'Reykjavík' },
    ],
  },
  {
    name: 'Pacific crossing',
    stops: [
      { country: 'CAN', city: 'Vancouver' },
      { country: 'JPN', city: 'Sapporo' },
    ],
  },
]

function fail(message: string): never {
  throw new ContentValidationError(`ocean crossings: ${message}`)
}

function cityOf(ref: CityRef): City {
  const country = COUNTRIES.get(ref.country as CountryCode) ?? fail(`unknown country ${ref.country}`)
  const matches = country.cities.filter((city) => city.name === ref.city)
  if (matches.length !== 1) fail(`${ref.country} has ${matches.length} cities named ${ref.city}`)
  return matches[0] as City
}

/** Every leg as an ALL_CITIES index pair [a, b], a < b, ascending, each a World network candidate. */
function loadLegs(): readonly (readonly [number, number])[] {
  const indexOf = new Map<City, number>(ALL_CITIES.map((city, index) => [city, index]))
  const candidates = new Set<string>(WORLD_NETWORK_CANDIDATES.map(([a, b]) => `${a}:${b}`))
  const legs = new Map<string, readonly [number, number]>()
  for (const crossing of OCEAN_CROSSINGS) {
    if (crossing.stops.length < 2) fail(`${crossing.name} needs two stops`)
    const stops = crossing.stops.map((ref) => indexOf.get(cityOf(ref)) ?? fail(`${ref.city} is not in ALL_CITIES`))
    for (let i = 1; i < stops.length; i++) {
      const a = Math.min(stops[i - 1] as number, stops[i] as number)
      const b = Math.max(stops[i - 1] as number, stops[i] as number)
      const key = `${a}:${b}`
      if (!candidates.has(key)) fail(`${crossing.name} leg ${(ALL_CITIES[a] as City).name}–${(ALL_CITIES[b] as City).name} is not a World network candidate`)
      legs.set(key, [a, b])
    }
  }
  return [...legs.values()].sort((x, y) => (x[0] !== y[0] ? x[0] - y[0] : x[1] - y[1]))
}

export const WORLD_OCEAN_CROSSING_LEGS: readonly (readonly [number, number])[] = loadLegs()
